import { supabase } from '@/integrations/supabase/client';

export interface ProfessionSpecialization {
  id: string;           // UUID
  professionId: string; // UUID - links to professions.id
  label: string;        // Hebrew label
  label_en?: string;    // English translation
  label_fr?: string;    // French translation
  label_ru?: string;    // Russian translation
}

export interface Profession {
  id: string;           // UUID
  slug: string;         // text profession_id (e.g., 'electrician')
  label: string;        // Hebrew label
  label_en?: string;    // English translation
  label_fr?: string;    // French translation
  label_ru?: string;    // Russian translation
  specializations: ProfessionSpecialization[];
}

// Cache for professions data
let professionsCache: Profession[] | null = null;
let specializationsCache: Map<string, ProfessionSpecialization> = new Map();
let specializationBySlugCache: Map<string, ProfessionSpecialization> = new Map();
let professionBySlugCache: Map<string, Profession> = new Map();
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// localStorage keys - v3 fixes missing translation columns in select query
const LOCAL_STORAGE_KEY = 'professions_cache_v3';
const LOCAL_STORAGE_TIMESTAMP_KEY = 'professions_cache_v3_ts';
const LOCAL_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Populate all caches from professions array
 */
const populateCaches = (professions: Profession[]): void => {
  professionsCache = professions;
  cacheTimestamp = Date.now();
  
  professionBySlugCache.clear();
  specializationsCache.clear();
  specializationBySlugCache.clear();
  
  professions.forEach(prof => {
    professionBySlugCache.set(prof.slug, prof);
    professionBySlugCache.set(prof.id, prof);
    
    prof.specializations.forEach(spec => {
      specializationsCache.set(spec.id, spec);
    });
  });
};

/**
 * Load professions from localStorage (SYNC - call before React renders)
 */
export const loadProfessionsFromLocalStorage = (): boolean => {
  try {
    const timestamp = localStorage.getItem(LOCAL_STORAGE_TIMESTAMP_KEY);
    if (!timestamp || Date.now() - parseInt(timestamp) > LOCAL_CACHE_DURATION) {
      return false;
    }
    const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!cached) return false;
    
    const professions: Profession[] = JSON.parse(cached);
    if (professions && professions.length > 0) {
      populateCaches(professions);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Save professions to localStorage
 */
const saveToLocalStorage = (professions: Profession[]): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(professions));
    localStorage.setItem(LOCAL_STORAGE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Silently fail - localStorage might be full
  }
};

/**
 * Fetch all professions with their specializations from the database
 * Uses the NEW `professions` table (not main_professions)
 */
export const fetchProfessionsFromDB = async (): Promise<Profession[]> => {
  // Return cache if fresh
  if (professionsCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return professionsCache;
  }

  try {
    // Fetch from NEW professions table (including translation columns)
    const { data: profs, error: profsError } = await supabase
      .from('professions')
      .select('id, profession_id, label, label_en, label_fr, label_ru, is_active, display_order')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (profsError) {
      console.error('Error fetching professions:', profsError);
      return [];
    }

    // Fetch specializations with translation columns
    const { data: specs, error: specsError } = await supabase
      .from('profession_specializations')
      .select('id, profession_id, specialization_id, label, label_en, label_fr, label_ru, is_active, display_order')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (specsError) {
      console.error('Error fetching profession specializations:', specsError);
      return [];
    }

    // Build the professions with specializations (including translations)
    const professions: Profession[] = profs.map(prof => {
      const profSpecs = specs
        .filter(spec => spec.profession_id === prof.id) // UUID match
        .map(spec => ({
          id: spec.id,           // UUID
          professionId: prof.id, // UUID
          label: spec.label,
          label_en: spec.label_en,
          label_fr: spec.label_fr,
          label_ru: spec.label_ru
        }));

      return {
        id: prof.id,              // UUID
        slug: prof.profession_id, // text slug (e.g., 'electrician')
        label: prof.label,
        label_en: prof.label_en,
        label_fr: prof.label_fr,
        label_ru: prof.label_ru,
        specializations: profSpecs
      };
    });

    // Also fetch specialization slugs for slug-based lookup
    const { data: specsFull } = await supabase
      .from('profession_specializations')
      .select('id, specialization_id, label, profession_id')
      .eq('is_active', true);
    
    // Add specialization slugs to professions for localStorage caching
    professions.forEach(prof => {
      prof.specializations.forEach(spec => {
        const specData = specsFull?.find(s => s.id === spec.id);
        if (specData?.specialization_id) {
          (spec as any).slug = specData.specialization_id;
        }
      });
    });

    // Populate all caches
    populateCaches(professions);
    
    // Also populate specialization slug cache
    professions.forEach(prof => {
      prof.specializations.forEach(spec => {
        if ((spec as any).slug) {
          specializationBySlugCache.set((spec as any).slug, spec);
        }
      });
    });
    
    // Save to localStorage for instant loading on next visit
    saveToLocalStorage(professions);

    return professions;
  } catch (error) {
    console.error('Error in fetchProfessionsFromDB:', error);
    return [];
  }
};

/**
 * Initialize the professions cache
 */
export const initializeProfessionsCacheNew = async (): Promise<void> => {
  // If already loaded from localStorage, just refresh in background
  if (professionsCache && professionsCache.length > 0) {
    fetchProfessionsFromDB(); // Don't await - refresh in background
    return;
  }
  await fetchProfessionsFromDB();
};

/**
 * Get profession by UUID or slug
 */
export const getProfessionById = (idOrSlug: string): Profession | null => {
  return professionBySlugCache.get(idOrSlug) || null;
};

/**
 * Pick the label for a language, falling back to Hebrew when a translation is missing
 */
const localizedLabel = (item: Profession | ProfessionSpecialization, lang?: string): string => {
  switch (lang) {
    case 'en':
      return item.label_en || item.label;
    case 'fr':
      return item.label_fr || item.label;
    case 'ru':
      return item.label_ru || item.label;
    default:
      return item.label; // Hebrew default
  }
};

/**
 * Get profession label by UUID or slug - with language support
 */
export const getProfessionLabelNew = (idOrSlug: string, lang?: string): string => {
  const prof = professionBySlugCache.get(idOrSlug);
  if (!prof) return idOrSlug;
  return localizedLabel(prof, lang);
};

/**
 * Get display label for any profession or specialization ID
 * Returns formatted label like "אינסטלטור - פתיחת סתימות" for specializations
 * Or just the profession label for main professions
 */
export const getDisplayLabel = (idOrSlug: string): string => {
  // First try as profession
  const prof = professionBySlugCache.get(idOrSlug);
  if (prof) {
    return prof.label;
  }
  
  // Try as specialization UUID
  const spec = specializationsCache.get(idOrSlug);
  if (spec) {
    const parentProf = professionBySlugCache.get(spec.professionId);
    if (parentProf) {
      return `${parentProf.label} - ${spec.label}`;
    }
    return spec.label;
  }
  
  // Try as specialization slug
  const specBySlug = specializationBySlugCache.get(idOrSlug);
  if (specBySlug) {
    const parentProf = professionBySlugCache.get(specBySlug.professionId);
    if (parentProf) {
      return `${parentProf.label} - ${specBySlug.label}`;
    }
    return specBySlug.label;
  }
  
  // Fallback - return as-is if nothing found
  return idOrSlug;
};

/**
 * Get specialization by UUID
 */
export const getSpecializationById = (id: string): ProfessionSpecialization | null => {
  return specializationsCache.get(id) || null;
};

/**
 * Get specialization label by UUID or slug - with language support
 */
export const getSpecializationLabel = (idOrSlug: string, lang?: string): string | null => {
  // First try by UUID, then by slug (e.g., 'intercom-install', 'cameras')
  const spec = specializationsCache.get(idOrSlug) || specializationBySlugCache.get(idOrSlug);
  return spec ? localizedLabel(spec, lang) : null;
};

/**
 * Get all specializations for a specific profession UUID
 */
export const getSpecializationsForProfession = (professionId: string): ProfessionSpecialization[] => {
  const prof = professionBySlugCache.get(professionId);
  return prof?.specializations || [];
};

/**
 * Filter specialization IDs to only those belonging to a specific profession
 */
export const filterSpecializationsForProfession = (
  specializationIds: string[],
  professionId: string
): string[] => {
  if (!specializationIds || specializationIds.length === 0) return [];
  
  const professionSpecs = getSpecializationsForProfession(professionId);
  const validSpecIds = new Set(professionSpecs.map(s => s.id));
  
  return specializationIds.filter(id => validSpecIds.has(id));
};

/**
 * Check if a profession ID (UUID or slug) exists
 */
export const isProfession = (id: string): boolean => {
  return professionBySlugCache.has(id);
};

/**
 * Get all professions (cached)
 */
export const getAllProfessions = (): Profession[] => {
  return professionsCache || [];
};

/**
 * Get all profession options for dropdowns (includes "all" option)
 * Includes translation columns for multi-language support
 */
export const getProfessionOptionsForSearch = (): Array<{
  id: string;
  label: string;
  label_en?: string;
  label_fr?: string;
  label_ru?: string;
  type: 'all' | 'main' | 'sub';
  professionId?: string;
}> => {
  const options: Array<{
    id: string;
    label: string;
    label_en?: string;
    label_fr?: string;
    label_ru?: string;
    type: 'all' | 'main' | 'sub';
    professionId?: string;
  }> = [];

  // Add "All" option with translations
  options.push({
    id: 'all',
    label: 'כל המקצועות',
    label_en: 'All Professions',
    label_fr: 'Toutes les professions',
    label_ru: 'Все профессии',
    type: 'all'
  });

  // Add professions and their specializations with translations
  const profs = professionsCache || [];
  profs.forEach(prof => {
    options.push({
      id: prof.id, // UUID
      label: prof.label,
      label_en: prof.label_en,
      label_fr: prof.label_fr,
      label_ru: prof.label_ru,
      type: 'main'
    });

    prof.specializations.forEach(spec => {
      options.push({
        id: spec.id, // UUID
        label: `${prof.label} - ${spec.label}`,
        label_en: prof.label_en && spec.label_en ? `${prof.label_en} - ${spec.label_en}` : undefined,
        label_fr: prof.label_fr && spec.label_fr ? `${prof.label_fr} - ${spec.label_fr}` : undefined,
        label_ru: prof.label_ru && spec.label_ru ? `${prof.label_ru} - ${spec.label_ru}` : undefined,
        type: 'sub',
        professionId: prof.id
      });
    });
  });

  return options;
};

/**
 * Find which profession a specialization belongs to
 */
export const getProfessionForSpecialization = (specId: string): Profession | null => {
  // Try by UUID first
  const spec = specializationsCache.get(specId);
  if (spec) {
    return professionBySlugCache.get(spec.professionId) || null;
  }
  
  // Try by slug
  const specBySlug = specializationBySlugCache.get(specId);
  if (specBySlug) {
    return professionBySlugCache.get(specBySlug.professionId) || null;
  }
  
  return null;
};

/**
 * Alias for getProfessionForSpecialization
 */
export const getMainProfessionForSpecialization = getProfessionForSpecialization;

/**
 * Get profession options formatted for form dropdowns
 * Returns options with SLUG as ID for URL compatibility
 * Includes translation columns for multi-language support
 */
export const getProfessionOptionsForForm = async (): Promise<Array<{
  id: string;
  label: string;
  label_en?: string;
  label_fr?: string;
  label_ru?: string;
  type: 'main' | 'sub' | 'all';
  mainProfessionId?: string;
  searchTerms?: string[];
}>> => {
  await fetchProfessionsFromDB();

  const options: Array<{
    id: string;
    label: string;
    label_en?: string;
    label_fr?: string;
    label_ru?: string;
    type: 'main' | 'sub' | 'all';
    mainProfessionId?: string;
    searchTerms?: string[];
  }> = [
    { id: 'all', label: 'כל המקצועות', label_en: 'All Professions', label_fr: 'Toutes les professions', label_ru: 'Все профессии', type: 'all' }
  ];

  const profs = professionsCache || [];
  profs.forEach(prof => {
    // Add main profession with SLUG as ID (for URL compatibility)
    // Include all translation columns for static multi-language support
    options.push({
      id: prof.slug, // Use slug like 'electrician' for URL
      label: prof.label,
      label_en: prof.label_en,
      label_fr: prof.label_fr,
      label_ru: prof.label_ru,
      type: 'main',
      searchTerms: [prof.label, prof.slug, prof.label_en, prof.label_fr, prof.label_ru].filter(Boolean) as string[]
    });

    // Note: specializations are not added for the main form
    // They are handled separately if needed
  });

  return options;
};
